import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const initialCurrentConversation = {
  id: null,
  type: null,
  chatbotId: null,
  interfaceType: null
};

const initialState = {
  currentConversation: { ...initialCurrentConversation },
  takeoverId: null,
  conversations: {}, // Store conversations by chatbot ID
};

// Helper function to determine which storage to use based on interface type
const getStorageForInterface = (interfaceType) => {
  // Use sessionStorage for bubble, direct-link, and embed interfaces
  // This ensures fresh conversations on page reload/re-run (like W3Schools)
  // while maintaining conversation during the same browsing session
  const sessionStorageInterfaces = ['bubble', 'direct-link', 'embed'];
  
  const useSessionStorage = sessionStorageInterfaces.includes(interfaceType);
  const storage = useSessionStorage ? sessionStorage : localStorage;
  
  return storage;
};

// Helper function to migrate data from the old store format to the new one
const migrateOldConversationData = (chatbotId, interfaceType) => {
  try {
    const storage = getStorageForInterface(interfaceType);
    
    // Try to get data from the old store
    const oldStoreKey = 'conversation-store';
    const oldStoreData = localStorage.getItem(oldStoreKey);
    
    if (oldStoreData) {
      const parsedData = JSON.parse(oldStoreData);
      
      // Check if the data has a state property (Zustand format)
      if (parsedData && parsedData.state) {
        const { currentConversation, takeoverId } = parsedData.state;
        
        // Only migrate if there's a current conversation
        if (currentConversation && currentConversation.id) {
          // Create the new store data with the migrated conversation
          const migratedData = {
            state: {
              currentConversation: {
                ...currentConversation,
                chatbotId,
                interfaceType
              },
              takeoverId,
              conversations: {},
              chatbotId,
              interfaceType
            }
          };
          
          // Save to the new store (using appropriate storage)
          const newStoreKey = `conversation-store-${chatbotId}-${interfaceType}`;
          storage.setItem(newStoreKey, JSON.stringify(migratedData));
          
          // Clear the old store to prevent further migrations
          localStorage.removeItem(oldStoreKey);
          
          return true;
        }
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error migrating conversation data:', error);
    return false;
  }
};

// Create a store factory function that accepts a chatbot ID and interface type
export const createConversationStore = (chatbotId, interfaceType = 'in-app') => {
  // Generate a unique name for the store based on chatbot ID and interface type
  const storeName = `conversation-store-${chatbotId}-${interfaceType}`;
  
  // Determine which storage to use based on interface type
  const storage = getStorageForInterface(interfaceType);
  
  // Try to migrate data from the old store format
  migrateOldConversationData(chatbotId, interfaceType);
  
  return create(
    persist(
      (set, get) => ({
        ...initialState,
        chatbotId,
        interfaceType,
        setCurrentConversation: (conversation) => {
          set({ 
            currentConversation: { 
              ...conversation, 
              chatbotId, 
              interfaceType 
            } 
          });
        },
        setTakeoverId: (id) => set({ takeoverId: id }),
        resetCurrentConversation: () => {
          set({ currentConversation: { ...initialCurrentConversation, chatbotId, interfaceType } });
        },
        reset: () => set({ ...initialState, chatbotId, interfaceType }),
        // Add a conversation to the store by chatbot ID
        addConversationToChatbot: (conversation) => {
          const conversations = { ...get().conversations };
          if (!conversations[chatbotId]) {
            conversations[chatbotId] = [];
          }
          conversations[chatbotId].push(conversation);
          set({ conversations });
        },
        // Get conversations for the current chatbot
        getConversationsForChatbot: () => {
          return get().conversations[chatbotId] || [];
        },
        // Clear conversations for a specific chatbot
        clearConversationsForChatbot: (targetChatbotId = chatbotId) => {
          const conversations = { ...get().conversations };
          delete conversations[targetChatbotId];
          set({ conversations });
        }
      }),
      {
        name: storeName,
        storage: createJSONStorage(() => storage)
      }
    )
  );
};

  // Store registry to ensure same instances are used across components
  const storeRegistry = new Map();

  // Function to get or create a store instance
  export const getConversationStore = (chatbotId, interfaceType = 'in-app') => {
    const storeKey = `${chatbotId}-${interfaceType}`;
    
    if (!storeRegistry.has(storeKey)) {
      storeRegistry.set(storeKey, createConversationStore(chatbotId, interfaceType));
    }
    
    return storeRegistry.get(storeKey);
  };

// For backward compatibility, export a default store instance (not a hook)
// Note: Prefer using the useConversationStore hook from '@/hooks/useConversationStore' instead
export const defaultConversationStore = createConversationStore('default', 'in-app');
